/**
 * VipTiersPage — VIP 等级积分设置页
 * 管理员可配置各等级门槛、折扣率、特权，并一键重算全量客户等级。
 * 基于 PRD §8 定义（钻石系，5 级）。
 */
import { useState } from "react";
import { useNotify } from "ra-core";
import { Crown, Save, RotateCcw, RefreshCw, Info, Star, TrendingUp, Settings2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getSupabaseClient } from "../providers/supabase/supabase";
import {
  useConfigurationContext,
  useConfigurationUpdater,
  type VipThresholds,
} from "../root/ConfigurationContext";
import { defaultVipThresholds } from "../root/defaultConfiguration";

// ── Tier definitions ──────────────────────────────────────────────────────────

type Priority = "标准" | "优先" | "高优先" | "最高优先";

type TierMeta = {
  key: keyof VipThresholds | "standard";
  name: string;          // 中文名
  nameEn: string;        // 英文名
  icon: string;
  color: string;         // badge className
  defaultDiscount: number; // 折扣百分比 (0 = 无折扣)
  priority: Priority;
};

const TIER_META: TierMeta[] = [
  { key: "standard", name: "注册客户", nameEn: "Registered", icon: "",   color: "bg-gray-100 text-gray-600 border-gray-200",      defaultDiscount: 0,  priority: "标准" },
  { key: "silver",   name: "银牌",     nameEn: "Silver",     icon: "🥈", color: "bg-slate-100 text-slate-700 border-slate-200",   defaultDiscount: 3,  priority: "标准" },
  { key: "gold",     name: "金牌",     nameEn: "Gold",       icon: "🥇", color: "bg-yellow-100 text-yellow-700 border-yellow-200", defaultDiscount: 6,  priority: "优先" },
  { key: "platinum", name: "铂金",     nameEn: "Platinum",   icon: "💎", color: "bg-purple-100 text-purple-700 border-purple-200", defaultDiscount: 12, priority: "高优先" },
  { key: "diamond",  name: "钻石",     nameEn: "Diamond",    icon: "👑", color: "bg-sky-100 text-sky-700 border-sky-200",         defaultDiscount: 0,  priority: "最高优先" },
];

// ── Main Page ─────────────────────────────────────────────────────────────────

export const VipTiersPage = () => {
  const notify = useNotify();
  const config = useConfigurationContext();
  const updateConfig = useConfigurationUpdater();

  // Editable thresholds (non-standard tiers only)
  const [thresholds, setThresholds] = useState<VipThresholds>({
    ...defaultVipThresholds,
    ...config.vipThresholds,
  });

  // Editable discount rates per tier
  const defaultDiscounts: Record<string, number> = {};
  TIER_META.forEach((t) => { defaultDiscounts[t.key] = t.defaultDiscount; });
  const [discounts, setDiscounts] = useState<Record<string, number>>(
    (config as any).vipDiscounts ?? defaultDiscounts,
  );

  // Diamond discount mode: "negotiate" = 另行议价, "percent" = 百分比
  const [diamondMode, setDiamondMode] = useState<"negotiate" | "percent">(
    (config as any).diamondDiscountMode ?? "negotiate",
  );

  // Points earn ratio: N points per 1 currency unit (default 1)
  const [pointsPerUnit, setPointsPerUnit] = useState<number>(
    (config as any).pointsPerUnit ?? 1,
  );

  const [currency, setCurrency] = useState(config.vipAccountingCurrency ?? "USD");
  const [saving, setSaving] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [dirty, setDirty] = useState(false);

  const handleThreshold = (key: keyof VipThresholds, val: string) => {
    setThresholds((prev) => ({ ...prev, [key]: Number(val) || 0 }));
    setDirty(true);
  };

  const handleDiscount = (key: string, val: string) => {
    setDiscounts((prev) => ({ ...prev, [key]: Number(val) || 0 }));
    setDirty(true);
  };

  const handleSave = async () => {
    setSaving(true);
    const newConfig = {
      ...config,
      vipThresholds: thresholds,
      vipAccountingCurrency: currency,
      vipDiscounts: discounts,
      diamondDiscountMode: diamondMode,
      pointsPerUnit,
    };
    const { error } = await getSupabaseClient()
      .from("configuration")
      .update({ config: newConfig })
      .eq("id", 1);

    if (error) {
      notify("保存失败: " + error.message, { type: "error" });
    } else {
      updateConfig(newConfig);
      notify("VIP 配置已保存", { type: "success" });
      setDirty(false);
    }
    setSaving(false);
  };

  const handleReset = () => {
    setThresholds({ ...defaultVipThresholds });
    setDiscounts({ ...defaultDiscounts });
    setDiamondMode("negotiate");
    setPointsPerUnit(1);
    setDirty(true);
  };

  const handleRecalculate = async () => {
    setRecalculating(true);
    const { data, error } = await getSupabaseClient().rpc("recalculate_all_vip_levels");
    if (error) {
      notify("重算失败: " + error.message, { type: "error" });
    } else {
      notify(`已重算 ${data} 位客户的 VIP 等级`, { type: "success" });
    }
    setRecalculating(false);
  };

  return (
    <div className="p-6 space-y-6 max-w-4xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Crown className="w-6 h-6 text-yellow-500" /> VIP 等级积分设置
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            基于钻石系命名方案 · 5 级体系 · 门槛和折扣率可调整
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={handleReset} className="gap-1">
            <RotateCcw className="w-3.5 h-3.5" /> 恢复默认
          </Button>
          <Button size="sm" onClick={handleSave} disabled={saving || !dirty} className="gap-1">
            <Save className="w-3.5 h-3.5" />
            {saving ? "保存中..." : "保存配置"}
          </Button>
        </div>
      </div>

      {/* Accounting Currency */}
      <div className="bg-card border rounded-lg p-4 flex items-center gap-4">
        <div className="flex-1">
          <p className="text-sm font-medium">记账货币</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            VIP 等级按此货币的累计消费金额计算。其他货币按面值累加。
          </p>
        </div>
        <select
          value={currency}
          onChange={(e) => { setCurrency(e.target.value); setDirty(true); }}
          className="rounded-md border border-input bg-background px-3 py-1.5 text-sm"
        >
          {["USD", "EUR", "GBP", "CAD"].map((c) => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>
      </div>

      {/* Tier Table */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-muted/50 border-b flex items-center justify-between">
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            等级详情
          </h2>
          <span className="flex items-center gap-1 text-xs text-muted-foreground">
            <Info className="w-3.5 h-3.5" />
            修改门槛后需点击「重算等级」使数据生效
          </span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs text-muted-foreground">
                <th className="text-left px-4 py-3 w-36">等级</th>
                <th className="px-3 py-3 text-center w-36">
                  消费门槛 ({currency})
                </th>
                <th className="px-3 py-3 text-center w-28">折扣率 (%)</th>
                <th className="px-3 py-3 text-center w-28">生产优先级</th>
                <th className="px-3 py-3 text-center w-24">专属客服</th>
              </tr>
            </thead>
            <tbody>
              {TIER_META.map((tier, i) => {
                const isStandard = tier.key === "standard";
                const threshold = isStandard ? 0 : thresholds[tier.key as keyof VipThresholds];
                const discount = discounts[tier.key] ?? tier.defaultDiscount;
                return (
                  <tr key={tier.key} className={i % 2 === 0 ? "" : "bg-muted/20"}>
                    {/* Tier name */}
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border ${tier.color}`}>
                        {tier.icon} {tier.name} {tier.nameEn}
                      </span>
                    </td>

                    {/* Threshold */}
                    <td className="px-3 py-3 text-center">
                      {isStandard ? (
                        <span className="text-muted-foreground text-xs">$0（默认）</span>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <span className="text-xs text-muted-foreground">$</span>
                          <input
                            type="number"
                            min={0}
                            step={100}
                            value={threshold}
                            onChange={(e) => handleThreshold(tier.key as keyof VipThresholds, e.target.value)}
                            className="w-24 rounded border border-input bg-background px-2 py-1 text-sm text-center"
                          />
                        </div>
                      )}
                    </td>

                    {/* Discount */}
                    <td className="px-3 py-3 text-center">
                      {isStandard ? (
                        <span className="text-muted-foreground text-xs">—</span>
                      ) : tier.key === "diamond" ? (
                        <div className="flex flex-col items-center gap-1.5">
                          <div className="flex items-center rounded-md border border-input overflow-hidden text-xs">
                            <button
                              type="button"
                              onClick={() => { setDiamondMode("negotiate"); setDirty(true); }}
                              className={`px-2.5 py-1 transition-colors ${diamondMode === "negotiate" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
                            >
                              另行议价
                            </button>
                            <button
                              type="button"
                              onClick={() => { setDiamondMode("percent"); setDirty(true); }}
                              className={`px-2.5 py-1 transition-colors ${diamondMode === "percent" ? "bg-primary text-primary-foreground" : "bg-background text-muted-foreground hover:bg-muted"}`}
                            >
                              百分比
                            </button>
                          </div>
                          {diamondMode === "percent" && (
                            <div className="flex items-center justify-center gap-1">
                              <input
                                type="number"
                                min={0}
                                max={50}
                                step={1}
                                value={discount}
                                onChange={(e) => handleDiscount(tier.key, e.target.value)}
                                className="w-16 rounded border border-input bg-background px-2 py-1 text-sm text-center"
                              />
                              <span className="text-xs text-muted-foreground">%</span>
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex items-center justify-center gap-1">
                          <input
                            type="number"
                            min={0}
                            max={50}
                            step={1}
                            value={discount}
                            onChange={(e) => handleDiscount(tier.key, e.target.value)}
                            className="w-16 rounded border border-input bg-background px-2 py-1 text-sm text-center"
                          />
                          <span className="text-xs text-muted-foreground">%</span>
                        </div>
                      )}
                    </td>

                    {/* Priority */}
                    <td className="px-3 py-3 text-center">
                      <span className={`text-xs font-medium ${
                        tier.priority === "最高优先" ? "text-sky-600" :
                        tier.priority === "高优先" ? "text-purple-600" :
                        tier.priority === "优先" ? "text-yellow-600" :
                        "text-muted-foreground"
                      }`}>
                        {tier.priority}
                      </span>
                    </td>

                    {/* Account manager — all tiers share the same service account */}
                    <td className="px-3 py-3 text-center text-sm">✅</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Recalculate */}
      <div className="bg-card border rounded-lg p-4 flex items-center justify-between">
        <div>
          <p className="text-sm font-medium">重算全量客户等级</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            保存配置后，点此按钮将新门槛应用到所有现有客户。
          </p>
        </div>
        <Button
          variant="outline"
          size="sm"
          disabled={recalculating}
          onClick={handleRecalculate}
          className="gap-1.5 shrink-0"
        >
          <RefreshCw className={`w-3.5 h-3.5 ${recalculating ? "animate-spin" : ""}`} />
          {recalculating ? "重算中..." : "重算等级"}
        </Button>
      </div>

      {/* Points Calculation Rules */}
      <div className="bg-card border rounded-lg overflow-hidden">
        <div className="px-4 py-2.5 bg-muted/50 border-b flex items-center gap-2">
          <Star className="w-4 h-4 text-yellow-500 fill-yellow-400" />
          <h2 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            积分计算规则
          </h2>
        </div>
        <div className="p-4 space-y-5">

          {/* Earn section */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <TrendingUp className="w-4 h-4 text-green-600" />
              <span className="text-sm font-medium">积分获取方式</span>
            </div>
            <div className="space-y-2">
              <div className="flex items-start gap-3 p-3 rounded-md bg-green-50 dark:bg-green-950/20 border border-green-100 dark:border-green-900">
                <div className="shrink-0 w-7 h-7 rounded-full bg-green-100 dark:bg-green-900 flex items-center justify-center text-green-700 dark:text-green-400 text-xs font-bold mt-0.5">1</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium">订单完成积分</p>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    订单状态变更为「已完成」时自动触发，按订单总金额取整后乘以下方比率计算积分。
                  </p>
                  <div className="mt-2 flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-muted-foreground">积分比率：每 $1 =</span>
                    <input
                      type="number"
                      min={0.01}
                      max={100}
                      step={0.1}
                      value={pointsPerUnit}
                      onChange={(e) => {
                        const v = parseFloat(e.target.value);
                        if (!isNaN(v) && v > 0) { setPointsPerUnit(v); setDirty(true); }
                      }}
                      className="w-20 rounded border border-input bg-background px-2 py-1 text-sm text-center"
                    />
                    <span className="text-xs text-muted-foreground">积分</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1.5">
                    例：订单总额 <code className="px-1 py-0.5 bg-muted rounded text-xs">$1,560.00</code> → 获得 <span className="font-semibold text-green-700 dark:text-green-400">+{Math.round(1560 * pointsPerUnit).toLocaleString()} 积分</span>
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Currency note */}
          <div>
            <div className="flex items-center gap-1.5 mb-2">
              <Settings2 className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium">多货币积分说明</span>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b text-muted-foreground">
                    <th className="text-left py-1.5 pr-4">订单货币</th>
                    <th className="text-left py-1.5 pr-4">积分计算</th>
                    <th className="text-left py-1.5">消费统计桶</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  <tr>
                    <td className="py-1.5 pr-4 font-medium">USD</td>
                    <td className="py-1.5 pr-4">ROUND(total × {pointsPerUnit}) 积分</td>
                    <td className="py-1.5 text-muted-foreground">total_spent_usd</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 pr-4 font-medium">EUR</td>
                    <td className="py-1.5 pr-4">ROUND(total × {pointsPerUnit}) 积分</td>
                    <td className="py-1.5 text-muted-foreground">total_spent_eur</td>
                  </tr>
                  <tr>
                    <td className="py-1.5 pr-4 font-medium text-muted-foreground">其他货币</td>
                    <td className="py-1.5 pr-4">ROUND(total × {pointsPerUnit}) 积分</td>
                    <td className="py-1.5 text-muted-foreground">total_spent_usd（默认）</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              VIP 等级升级使用上方「记账货币」对应的消费桶数据（USD 或 EUR）。
            </p>
          </div>

        </div>
      </div>

      {/* Notes */}
      <div className="text-xs text-muted-foreground space-y-1 border-t pt-4">
        <p>⚠️ 门槛为参考值，修改后需点「保存配置」再点「重算等级」才会更新所有客户数据。</p>
        <p>💡 折扣率仅用于报价单自动折扣（功能开发中），不影响已有订单。</p>
        <p>💬 所有等级客户均对接同一专属客服账号，后续可扩展为多账号分配。</p>
        <p>🔒 钻石客户为战略客户，建议专属议价，折扣率设为 0 表示线下协商。</p>
      </div>

      {dirty && (
        <p className="text-xs text-orange-600 text-right">⚠ 有未保存的更改</p>
      )}
    </div>
  );
};

VipTiersPage.path = "/settings/vip";
